import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import LogoTray from "../LogoTray";
import "../../Search.css";
import "./Auth.css";
import { clearArticleCacheOnLogin, storeCurrentFilter } from "../../utils/sessionCache";

// Define API URL as a constant
const API_URL = process.env.REACT_APP_API_URL || "/api";

const Login = () => {
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!formData.identifier || !formData.password) {
      setError("Korisničko ime/email i lozinka su obavezni.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        await clearArticleCacheOnLogin();
        storeCurrentFilter(null);

        // Store token and user data
        localStorage.setItem("token", data.token);
        localStorage.setItem("isAdmin", data.admin);
        
        // Store user data in localStorage
        const userData = {
          username: data.username,
          admin: data.admin,
          current_filter: data.current_filter
        };
        localStorage.setItem("user", JSON.stringify(userData));
        
        setMessage("Prijava uspješna!");
        
        // After successful login, fetch user info
        try {
          const userResponse = await fetch(`${API_URL}/user/me`, {
            headers: { 
              "Authorization": `Bearer ${data.token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          if (!userResponse.ok) {
            throw new Error('Failed to fetch user data');
          }
          
          const userData = await userResponse.json();
          
          // Update stored user data with complete information
          localStorage.setItem("user", JSON.stringify(userData));
          
          storeCurrentFilter(userData.current_filter);
          
          // Set a timeout to refresh the token before it expires
          setTimeout(() => {
            refreshToken();
          }, 45 * 60 * 1000); // Refresh 45 minutes after login (token expires in 1 hour)
          
          const returnTo = location.state?.returnTo;
          navigate(typeof returnTo === 'string' && (returnTo === '/delete-account' || /^\/notifications\/\d+\/[a-zA-Z0-9-]+$/.test(returnTo)) ? returnTo : '/');
        } catch (userErr) {
          console.error('Error fetching user data:', userErr);
          setError("Prijava uspješna, ali došlo je do greške pri dohvaćanju korisničkih podataka. Molimo osvježite stranicu.");
        }
      } else {
        // Handle different error status codes
        if (response.status === 500) {
          setError("Došlo je do greške na serveru. Molimo pokušajte ponovo za nekoliko trenutaka.");
        } else if (response.status === 401) {
          setError("Pogrešno korisničko ime ili lozinka.");
        } else if (response.status === 429) {
          setError("Previše pokušaja prijave. Molimo pričekajte prije ponovnog pokušaja.");
        } else {
          setError(data.error || data.message || "Prijava nije uspjela. Molimo pokušajte ponovo.");
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError("Nije moguće povezati se sa serverom. Molimo provjerite svoju internetsku vezu i pokušajte ponovo.");
      } else {
        setError("Došlo je do neočekivane greške. Molimo pokušajte ponovo.");
      }
    }
  };

  // Function to refresh the token
  const refreshToken = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/refresh-token`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.token);
        
        // Schedule next refresh
        setTimeout(() => {
          refreshToken();
        }, 45 * 60 * 1000);
      } else {
        // If refresh fails, log out the user
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("currentFilter");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      // If refresh fails, log out the user
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAdmin");
      localStorage.removeItem("currentFilter");
      navigate("/login");
    }
  };

  return (
    <>
      <LogoTray />
      <div className="auth-container">
        <div className="auth-box">
        <div className="tech-border">
          <div className="auth-content">
            <h2 className="text-center mb-4">Prijava</h2>
            {message && <div className="alert alert-success text-center">{message}</div>}
            {error && <div className="alert alert-danger text-center">{error}</div>}
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group mb-3">
                <label>Korisničko ime ili Email</label>
                <input
                  type="text"
                  name="identifier"
                  value={formData.identifier}
                  onChange={handleChange}
                  required
                  className="form-control tech-input"
                  placeholder="Unesite korisničko ime ili email"
                />
              </div>
              <div className="form-group mb-4">
                <label>Lozinka</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="form-control tech-input"
                    placeholder="Unesite lozinku"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary w-100 tech-button">
                Prijavi se
              </button>
            </form>
            <p className="text-center mt-3">
              <Link to="/forgot-password" className="tech-link">
                Zaboravili ste lozinku?
              </Link>
            </p>
            <p className="text-center mt-3">
              Nemate račun?{" "}
              <Link to="/signup" className="tech-link">
                Registrujte se ovdje
              </Link>
            </p>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Login;
